import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import Config from '../../../../Config';
import useExtendedEffect from '../../../../utilities/hooks/useExtendedEffect';
import CheckBox from '../../../Shared/CheckBox/Checkbox';
import ThirdParty from '../../../Shared/ThirdParty/ThirdParty';
import TextInput from '../../../Shared/TextInput/TextInput';
import SaleLoader from '../../../Shared/SaleLoader/SaleLoader';
import PackageDetails from '../Partials/PackageDetails/PackageDetails';
import SaleFooter from '../Partials/SaleFooter/SaleFooter';
import Payment from '../Partials/Payment/Payment';
import PopUpPaymentError from '../Partials/Payment/PopUpPaymentError/PopUpPaymentError';
import SuccessMsg from '../Partials/Payment/SuccessMsg/SuccessMsg';
import ErrorMsg from '../Partials/Payment/ErrorMsg/ErrorMsg';
import InternetBanSelectView, {
  NEW_PAYMENT_OPTION,
  formatPaymentToken,
  getBanDescription,
} from '../Partials/InternetExistingPayment/InternetBanSelectView';
import { buildUrlAndOpenWindow } from '../../../../utilities/StringHelpers/StringHelpers';
import {
  goToPage,
  reportDWH,
  setFlowType,
  setSelectedUserAddress,
  setEmail,
  setErrorText,
  setIsReceivingAd,
  resetCgIframeUrl,
} from '../saleJetBlockSlice';
import { getEMVStep1, getEMVStep2 } from '../saleBlockAsyncThunk';
import './internetExistingPaymentPage.scss';

const isValidEmail = (email) => /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email);

const InternetExistingPaymentPage = (props) => {
  const [isBanSelectOpen, setIsBanSelectOpen] = useState(false);
  const [draftSelection, setDraftSelection] = useState(null);
  const [isConfirmTerms, setIsConfirmTerms] = useState(false);
  const [termsError, setTermsError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [forceAddPayment, setForceAddPayment] = useState(false);
  const [paymentStep, setPaymentStep] = useState('payment');
  const [paymentErrorMessage, setPaymentErrorMessage] = useState('');
  const [isDisplayErrorPopup, setIsDisplayErrorPopup] = useState(false);
  const [errorPopupContent, setErrorPopupContent] = useState({ title: '', subTitle: '' });
  // True while CG iframe is redirecting / booting callback page (before postMessage).
  const [isCgWaiting, setIsCgWaiting] = useState(false);
  const cgIframeLoadCountRef = useRef(0);
  const pageRootRef = useRef(null);
  const cgHandledRef = useRef(false);
  const cgResultHandlerRef = useRef(null);
  let messageHandler;

  const selectedBan = props.selectedAddress;
  const bans = props.userAddresses || [];
  const hasExistingBan = Boolean(selectedBan?.additionalInfo?.banId);
  const isAddPaymentMode = forceAddPayment || !hasExistingBan;
  const isProcessingPayment = isCgWaiting || paymentStep === 'loader';

  const showFiberPaymentError = (message) => {
    setIsCgWaiting(false);
    setPaymentErrorMessage(message);
    setPaymentStep('error');
    setIsDisplayErrorPopup(false);
  };

  // Reads the CG result from the iframe URL once it is back on our domain,
  // instead of waiting for the callback SPA to boot and postMessage.
  function readCgResultFromIframe() {
    const iframe = pageRootRef.current?.querySelector('iframe.nvc-iframe');
    const iframeWindow = iframe?.contentWindow;
    if (!iframeWindow) {
      return null;
    }

    let search = '';
    try {
      search = iframeWindow.location.search || '';
    } catch (error) {
      // Still on Credit Guard's domain — cross origin access is blocked.
      return null;
    }

    if (!search) {
      return null;
    }

    const query = new URLSearchParams(search);
    const paymentResponse = query.get('paymentResponse');
    if (paymentResponse !== 'success' && paymentResponse !== 'error') {
      return null;
    }

    return {
      status: paymentResponse,
      token: query.get('txId'),
      personalId: query.get('personalId'),
    };
  }

  const handleCgIframeLoad = () => {
    cgIframeLoadCountRef.current += 1;
    // First load = CG payment form. Further loads = user submitted / redirect chain.
    if (cgIframeLoadCountRef.current <= 1) {
      return;
    }

    setIsCgWaiting(true);
    const result = readCgResultFromIframe();
    if (result) {
      cgResultHandlerRef.current(result);
    }
  };

  useEffect(() => {
    props.reportDWH({
      actionType: isAddPaymentMode ? 'Add Payment Method' : 'Existing Payment Method',
      status: 'Ongoing',
      messageText: isAddPaymentMode
        ? 'Internet Add Payment Page Displayed'
        : 'Internet Existing Payment Page Displayed',
    });
  }, [isAddPaymentMode]);

  useEffect(() => {
    if (!forceAddPayment && !selectedBan?.additionalInfo?.banId && bans.length > 0) {
      props.setSelectedUserAddress({
        address: bans[0],
        isSelectedSeparateInvoice: false,
      });
    }
  }, [bans, selectedBan?.additionalInfo?.banId, forceAddPayment]);

  useExtendedEffect(
    (runSideEffects) => {
      if (!isAddPaymentMode) {
        return;
      }
      startCgPayment();
      if (!runSideEffects()) {
      }
    },
    [isAddPaymentMode],
  );

  const billingDetails = useMemo(() => {
    if (!selectedBan) {
      return null;
    }
    const info = selectedBan.additionalInfo || {};
    const addressValue = info.billingAddress || selectedBan.billingAddress || '';
    if (!addressValue) {
      return null;
    }
    if (info.billingAddressType === 'EMAIL' || String(addressValue).includes('@')) {
      return { label: 'החשבונית תישלח למייל:', value: addressValue };
    }
    if (info.billingAddressType === 'SMS') {
      return { label: 'החשבונית תישלח במסרון למספר:', value: addressValue };
    }
    if (info.billingAddressType === 'ADDRESS') {
      return { label: 'החשבונית תישלח לכתובת:', value: addressValue };
    }
    return { label: 'החשבונית תישלח אליך:', value: addressValue };
  }, [selectedBan]);

  const paymentTokenDisplay = useMemo(() => {
    if (!selectedBan?.additionalInfo) {
      return '';
    }
    return formatPaymentToken(
      selectedBan.additionalInfo.paymentType,
      selectedBan.additionalInfo.paymentToken,
    );
  }, [selectedBan]);

  const serviceDescription = getBanDescription(selectedBan);

  const openBanSelect = () => {
    setDraftSelection(selectedBan?.additionalInfo?.banId || null);
    setIsBanSelectOpen(true);
  };

  const confirmBanSelect = () => {
    if (!draftSelection) {
      return;
    }

    if (draftSelection === NEW_PAYMENT_OPTION) {
      props.setFlowType('newCustomer');
      props.setSelectedUserAddress({
        address: {},
        isSelectedSeparateInvoice: true,
      });
      setForceAddPayment(true);
      setPaymentStep('payment');
      setIsConfirmTerms(false);
      props.reportDWH({
        actionType: 'Existing Payment Method',
        status: 'Ongoing',
        messageText: 'Add new payment method selected',
      });
      setIsBanSelectOpen(false);
      return;
    }

    const nextBan = bans.find((item) => item?.additionalInfo?.banId === draftSelection);
    if (nextBan) {
      props.setSelectedUserAddress({
        address: nextBan,
        isSelectedSeparateInvoice: false,
      });
      setForceAddPayment(false);
      props.reportDWH({
        actionType: 'Existing Payment Method',
        status: 'Ongoing',
        messageText: `Existing BAN selected: ${draftSelection}`,
      });
    }
    setIsBanSelectOpen(false);
  };

  const isPaymentApproved = paymentStep === 'success';
  const isAddPaymentExtrasDisabled = isAddPaymentMode && !isPaymentApproved;

  const termsCheckbox = (
    <CheckBox
      id={isAddPaymentMode ? 'internet-add-payment-terms' : 'internet-existing-payment-terms'}
      checked={isConfirmTerms}
      onCheck={() => {
        if (isAddPaymentExtrasDisabled) {
          return;
        }
        setIsConfirmTerms(true);
        setTermsError('');
      }}
      onUncheck={() => {
        if (isAddPaymentExtrasDisabled) {
          return;
        }
        setIsConfirmTerms(false);
      }}
      label={
        <>
          אישור{' '}
          <a
            href='#'
            onClick={(event) => {
              event.preventDefault();
              if (isAddPaymentExtrasDisabled) {
                return;
              }
              buildUrlAndOpenWindow(props.programDetailsLink);
            }}
          >
            עיקרי פרטי התכנית
          </a>
          ,{' '}
          <a
            href='#'
            onClick={(event) => {
              event.preventDefault();
              if (isAddPaymentExtrasDisabled) {
                return;
              }
              buildUrlAndOpenWindow(props.connectionAgreementLink);
            }}
          >
            הסכם ההתקשרות
          </a>{' '}
          ו
          <a
            href='#'
            onClick={(event) => {
              event.preventDefault();
              if (isAddPaymentExtrasDisabled) {
                return;
              }
              buildUrlAndOpenWindow(props.generalTermsAgreementLink);
            }}
          >
            התנאים הכלליים
          </a>
        </>
      }
    />
  );

  const adsCheckbox = (
    <CheckBox
      id={isAddPaymentMode ? 'internet-add-payment-ads' : 'internet-existing-payment-ads'}
      checked={props.isReceivingAd}
      onCheck={() => {
        if (isAddPaymentExtrasDisabled) {
          return;
        }
        props.setIsReceivingAd(true);
      }}
      onUncheck={() => {
        if (isAddPaymentExtrasDisabled) {
          return;
        }
        props.setIsReceivingAd(false);
      }}
      label={<ThirdParty />}
    />
  );

  const handleExistingContinue = () => {
    if (!isConfirmTerms) {
      setTermsError('כדי להמשיך יש לאשר את התנאים');
      return;
    }
    if (!selectedBan?.additionalInfo?.banId) {
      setTermsError('אנא בחרו חשבון לחיוב');
      return;
    }

    props.reportDWH({
      actionType: 'Existing Payment Method',
      status: 'Ongoing',
      messageText: 'Confirm and pay clicked',
    });
    props.goToPage('createSaleInProcess');
  };

  const handleAddPaymentContinue = () => {
    const email = (props.email || '').trim();
    if (!email) {
      setEmailError('אנא הכנס כתובת מייל');
      return;
    }
    if (!isValidEmail(email)) {
      setEmailError('אנא הכנס כתובת מייל תקינה');
      return;
    }
    if (!isConfirmTerms) {
      setTermsError('כדי להמשיך יש לאשר את התנאים');
      return;
    }
    if (paymentStep !== 'success') {
      return;
    }

    props.reportDWH({
      actionType: 'Add Payment Method',
      status: 'Ongoing',
      messageText: 'Add payment method button clicked',
    });
    props.goToPage('createSaleInProcess');
  };

  function startCgPayment() {
    cgIframeLoadCountRef.current = 0;
    cgHandledRef.current = false;
    setIsCgWaiting(false);
    setPaymentStep('payment');
    props.getEMVStep1();
    addCgMessageListener();
  }

  function handleCgResult(data) {
    if (cgHandledRef.current) {
      return;
    }
    cgHandledRef.current = true;
    removeCgMessageListener();

    if (data.status === 'success') {
      // Show loader immediately — the CG callback page may still be loading.
      setIsCgWaiting(false);
      setPaymentStep('loader');
      props.resetCgIframeUrl();
      if (
        Config.cgShowIdNumber == 'True' &&
        props.identity != '' &&
        data.personalId != props.identity
      ) {
        showFiberPaymentError('כרטיס האשראי אינו תואם למספר תעודת הזהות');
        props.reportDWH({
          actionType: 'Credit Card Info',
          status: 'Error',
          messageText: 'Credit Card Not Valid Error',
        });
        return;
      }
      confirmCgPayment(data.token, data.personalId);
      return;
    }

    props.resetCgIframeUrl();
    showFiberPaymentError('פרטי האשראי שהוזנו לא תקינים');
    props.reportDWH({
      actionType: 'Credit Card Info',
      status: 'Error',
      messageText: 'Credit Guard Error',
    });
  }

  cgResultHandlerRef.current = handleCgResult;

  async function confirmCgPayment(token, identity) {
    setIsCgWaiting(false);
    setPaymentStep('loader');
    const result = await props.getEMVStep2({ token, id: identity });
    if (result.payload.returnCode == '00') {
      setPaymentStep('success');
      return;
    }
    if (result.payload.errorCode == '803') {
      showFiberPaymentError(
        'לא ניתן להשתמש בכרטיס לחיוב מיידי מסוג דביט / דיירקט כאמצעי תשלום קבוע',
      );
      props.reportDWH({
        actionType: 'Credit Card Info',
        status: 'Error',
        messageText: 'Credit Card Not Valid Error - Debit',
      });
      return;
    }
    showFiberPaymentError('העסקה לא אושרה על ידי חברת האשראי');
  }

  function closePaymentErrorPopup() {
    setIsDisplayErrorPopup(false);
  }

  function retryCgPayment() {
    setPaymentErrorMessage('');
    startCgPayment();
    setIsDisplayErrorPopup(false);
  }

  function addCgMessageListener() {
    const eventMethod = window.addEventListener ? 'addEventListener' : 'attachEvent';
    const eventer = window[eventMethod];
    const messageEvent = eventMethod == 'attachEvent' ? 'onmessage' : 'message';

    // Fallback for when the iframe URL cannot be read (different origin than the SPA).
    messageHandler = function (e) {
      const isSuccess = e.data.status == 'success';
      const isError = e.data.status == 'error';

      if (isSuccess || isError) {
        cgResultHandlerRef.current({
          status: e.data.status,
          token: e.data.token,
          personalId: e.data.personalId,
        });
      }
    };

    eventer(messageEvent, messageHandler);
  }

  function removeCgMessageListener() {
    const hasAddEventListener = window.addEventListener;
    window[hasAddEventListener ? 'removeEventListener' : 'detachEvent'](
      hasAddEventListener ? 'message' : 'onmessage',
      messageHandler,
    );
  }

  const addPaymentDisableButton = !isPaymentApproved;
  const existingPaymentDisableButton = !selectedBan?.additionalInfo?.banId;

  if (isBanSelectOpen && bans.length > 0) {
    return (
      <div className='slideAnimation internet-existing-payment-page internet-existing-payment-page--ban-select'>
        <div className='package-details-wrapper package-details-wrapper--step-bar'>
          <PackageDetails />
        </div>

        <div className='page-headline-wrapper'>
          <h1 className='sale-title-big'>כאן אפשר לבחור איזה אמצעי תשלום נחייב</h1>
        </div>

        <div className='sale-page-content'>
          <InternetBanSelectView
            bans={bans}
            draftSelection={draftSelection}
            onSelectDraft={setDraftSelection}
          />

          <SaleFooter
            isDisplayFooter={true}
            btnText='אישור ותשלום'
            onclick={confirmBanSelect}
            formid='saleJet'
            disableButton={!draftSelection}
          />
        </div>
      </div>
    );
  }

  if (isAddPaymentMode) {
    return (
      <div
        ref={pageRootRef}
        className='slideAnimation internet-existing-payment-page internet-existing-payment-page--add'
      >
        <div className='package-details-wrapper package-details-wrapper--step-bar'>
          <PackageDetails />
        </div>

        <div className='page-headline-wrapper'>
          {isProcessingPayment ? (
            <h1 className='sale-title-big'>עוד כמה רגעים מסיימים</h1>
          ) : (
            <h1 className='sale-title-big'>זה הזמן להוסיף אמצעי תשלום</h1>
          )}
        </div>

        <div className='sale-page-content'>
          {/* Keep iframe mounted while waiting for CG postMessage (do not unmount on waiting). */}
          {paymentStep === 'payment' && (
            <div className={isCgWaiting ? 'internet-add-payment-cg--hidden' : undefined}>
              <Payment
                className='payment--fiber-add'
                hideInfo
                iframeHeight='340px'
                onIframeLoad={handleCgIframeLoad}
              />
            </div>
          )}
          {isProcessingPayment && (
            <div className='loader-wrapper'>
              <SaleLoader />
            </div>
          )}
          {paymentStep === 'success' && <SuccessMsg variant='fiberAdd' termsLink={props.termsLink} />}
          {paymentStep === 'error' && (
            <ErrorMsg
              variant='fiberAdd'
              message={paymentErrorMessage}
              onclickTryAgainBtn={retryCgPayment}
            />
          )}

          {(paymentStep === 'payment' || paymentStep === 'success') && !isCgWaiting && (
            <div
              className={`internet-add-payment-extras${
                isAddPaymentExtrasDisabled ? ' internet-add-payment-extras--disabled' : ''
              }`}
              aria-disabled={isAddPaymentExtrasDisabled}
            >
              <div className='internet-add-payment-extras__email'>
                <h2 className='internet-add-payment-extras__email-title'>לאיזה מייל לשלוח את החשבונית?</h2>
                <TextInput
                  placeholder='כתובת מייל'
                  error={emailError || props.errorText}
                  required='required'
                  disabled={isAddPaymentExtrasDisabled}
                  onChange={(e) => {
                    if (isAddPaymentExtrasDisabled) {
                      return;
                    }
                    props.setEmail(e.target.value);
                    setEmailError('');
                    props.setErrorText('');
                  }}
                  value={props.email}
                />
              </div>

              <div className='internet-existing-payment__checkboxes'>
                {termsCheckbox}
                {adsCheckbox}
                {termsError ? (
                  <div className='internet-existing-payment__checkbox-error'>{termsError}</div>
                ) : null}
              </div>
            </div>
          )}

          {!isProcessingPayment && (
            <SaleFooter
              isDisplayFooter={true}
              btnText='הוספת אמצעי תשלום'
              onclick={handleAddPaymentContinue}
              formid='saleJet'
              disableButton={addPaymentDisableButton}
            />
          )}

          <PopUpPaymentError
            isDisplayErrorPopup={isDisplayErrorPopup}
            content={errorPopupContent}
            closePopUp={closePaymentErrorPopup}
            onclickTryAgainBtn={retryCgPayment}
          />
        </div>
      </div>
    );
  }

  return (
    <div className='slideAnimation internet-existing-payment-page'>
      <div className='package-details-wrapper package-details-wrapper--step-bar'>
        <PackageDetails />
      </div>

      <div className='page-headline-wrapper'>
        <h1 className='sale-title-big'>זה הזמן לאשר את אופן התשלום</h1>
      </div>

      <div className='sale-page-content'>
        <div className='internet-existing-payment'>
          <div className='internet-existing-payment__card'>
            <h2 className='internet-existing-payment__card-title'>החיוב יתווסף לחשבון הקיים שלך</h2>
            {serviceDescription ? (
              <p className='internet-existing-payment__row'>{serviceDescription}</p>
            ) : null}
            {paymentTokenDisplay ? (
              <p className='internet-existing-payment__row'>
                אמצעי התשלום:{' '}
                <span className='internet-existing-payment__payment-token'>{paymentTokenDisplay}</span>
              </p>
            ) : null}
            {billingDetails ? (
              <div className='internet-existing-payment__billing'>
                <p className='internet-existing-payment__row'>{billingDetails.label}</p>
                <p className='internet-existing-payment__row internet-existing-payment__billing-value'>
                  {billingDetails.value}
                </p>
              </div>
            ) : null}
            <button
              type='button'
              className='internet-existing-payment__change-link'
              onClick={openBanSelect}
            >
              אני רוצה להוסיף את החיוב לחשבון אחר
            </button>
          </div>

          <div className='internet-existing-payment__checkboxes'>
            {termsCheckbox}
            {adsCheckbox}
            {termsError ? (
              <div className='internet-existing-payment__checkbox-error'>{termsError}</div>
            ) : null}
          </div>
        </div>

        <SaleFooter
          isDisplayFooter={true}
          btnText='אישור ותשלום'
          onclick={handleExistingContinue}
          formid='saleJet'
          disableButton={existingPaymentDisableButton}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userAddresses: state.saleJetBlock.userAddressPage.userAddresses,
  selectedAddress: state.saleJetBlock.userAddressPage.selectedAddress,
  programDetailsLink: state.saleJetBlock.selectedPackage.programDetailsLink,
  connectionAgreementLink: state.saleJetBlock.selectedPackage.connectionAgreementLink,
  generalTermsAgreementLink: state.saleJetBlock.saleBlockData.generalTermsAgreementLink,
  email: state.saleJetBlock.personalInfo.email,
  identity: state.saleJetBlock.personalInfo.identity,
  isReceivingAd: state.saleJetBlock.personalInfo.isReceivingAd,
  errorText: state.saleJetBlock.errorText,
  termsLink: state.saleJetBlock.saleBlockData.termsLink,
});

const mapDispatchToProps = {
  goToPage,
  reportDWH,
  setFlowType,
  setSelectedUserAddress,
  setEmail,
  setErrorText,
  setIsReceivingAd,
  resetCgIframeUrl,
  getEMVStep1,
  getEMVStep2,
};

export default connect(mapStateToProps, mapDispatchToProps)(InternetExistingPaymentPage);
